import { Text, VStack } from "@chakra-ui/react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { PopupGroupDeliverDayOfWeeks_popupGroup$key } from "~/src/__relay_artifacts__/PopupGroupDeliverDayOfWeeks_popupGroup.graphql";
import { Chip } from "~/src/components/common/Chip";
import { DefinitionTeam } from "~/src/components/common/DefinitionTeam";
import { formatISOToTime } from "~/src/lib/utils/date-utils";

type Props = {
  popupGroupRef: PopupGroupDeliverDayOfWeeks_popupGroup$key;
};

const fragment = graphql`
  fragment PopupGroupDeliverDayOfWeeks_popupGroup on PopupGroup {
    startDate
    endDate
    deliverDayOfWeeks {
      weekOfMonth
      daysOfWeek
    }
    schedules {
      id
      startTime
      endTime
    }
  }
`;

export const PopupGroupDeliverDayOfWeeks: React.VFC<Props> = ({
  popupGroupRef,
}) => {
  const popupGroup = useFragment(fragment, popupGroupRef);

  const dateDuration = React.useMemo(() => {
    if (popupGroup.startDate == null && popupGroup.endDate == null) return "";

    return `${popupGroup.startDate || ""} ~ ${popupGroup.endDate || ""}`;
  }, [popupGroup.endDate, popupGroup.startDate]);

  const deliverDayOfWeeks = React.useMemo(
    () =>
      popupGroup.deliverDayOfWeeks.map((deliverDayOfWeek) => {
        const weekOfMonth = deliverDayOfWeek.weekOfMonth
          ? `第${deliverDayOfWeek.weekOfMonth}週`
          : "毎週";
        const daysOfWeek = deliverDayOfWeek.daysOfWeek.map(
          (dayOfWeek) =>
            GraphQLEnums.DayOfWeek.find(({ value }) => value === dayOfWeek)
              ?.label
        );
        return `${weekOfMonth} ${daysOfWeek.join("・")}`;
      }),
    [popupGroup.deliverDayOfWeeks]
  );

  const schedules = React.useMemo(
    () =>
      (popupGroup.schedules || [])
        .map((schedule) => {
          const startTime = formatISOToTime(schedule.startTime);
          const endTime = formatISOToTime(schedule.endTime);
          if (!startTime && !endTime) return null;

          return {
            label: `${startTime || ""} ~ ${endTime || ""}`,
            value: schedule.id,
          };
        })
        .filter(Boolean),
    [popupGroup.schedules]
  );

  return (
    <>
      <DefinitionTeam label="実施期間" dense>
        {dateDuration}
      </DefinitionTeam>
      <DefinitionTeam label="実施時間" dense>
        {schedules.map((schedule) =>
          schedule ? <Chip key={schedule.value} label={schedule.label} /> : null
        )}
      </DefinitionTeam>
      <DefinitionTeam label="実施曜日" dense>
        <VStack alignItems={"flex-start"}>
          {deliverDayOfWeeks.map((deliverDayOfWeek, index) => (
            <Text key={index}>{deliverDayOfWeek}</Text>
          ))}
        </VStack>
      </DefinitionTeam>
    </>
  );
};
